import {
  CreateShopInput,
  CreateShopItemInput,
  CreateShopItemMutation,
  CreateShopMutation,
  CreateShopSectionMutation,
  DeleteShopItemMutation,
  DeleteShopMutation,
  DeleteShopSectionMutation,
  ReOrderShopItemInput,
  ReOrderShopItemsMutation,
  SortShopSectionMutation,
  UpdateShopInput,
  UpdateShopItemInput,
  UpdateShopItemMutation,
  UpdateShopMutation,
  UpdateShopSectionMutation,
  useCreateShopItemMutation,
  useCreateShopMutation,
  useCreateShopSectionMutation,
  useDeleteShopItemMutation,
  useDeleteShopMutation,
  useDeleteShopSectionMutation,
  useDuplicateShopItemMutation,
  useDuplicateShopMutation,
  useGetShopsQuery,
  useReOrderShopItemsMutation,
  useSortShopSectionMutation,
  useUpdateShopItemMutation,
  useUpdateShopMutation,
  useUpdateShopSectionMutation,
} from "@/graphql";
import useToast from "@/hooks/useToastNotification";
import { FetchResult } from "@apollo/client";
import { computed, ref, watch } from "vue";
import {
  CreateShopSectionPayload,
  Shop,
  ShopSection,
  SortShopSectionPayload,
  UpdateShopSectionPayload,
} from "../shop.types";

const { toastError, toastSuccess } = useToast();

export default function useShopsManager(appId: string, versionId: string) {
  const {
    result,
    loading: queryLoading,
    refetch: refetchShops,
  } = useGetShopsQuery({ appId, versionId }, { fetchPolicy: "network-only", notifyOnNetworkStatusChange: true });

  const shops = ref<Shop[]>([]);
  const activeShop = ref<Shop | null>(null);

  const initializeShops = (fetchedShops: Shop[]) => {
    shops.value = fetchedShops;
    const oldSelectedValue = activeShop.value && fetchedShops.find((shop: Shop) => shop.id === activeShop.value?.id);

    if (oldSelectedValue) {
      activeShop.value = oldSelectedValue;
    } else if (fetchedShops.length > 0) {
      [activeShop.value] = fetchedShops;
    }
  };

  watch(
    () => result.value?.getShops,
    (newShops: unknown) => {
      if (newShops) {
        initializeShops(newShops as Shop[]);
      }
    }
  );

  const shopsToDisplay = computed(() => shops.value);

  const { mutate: createShopMutation, loading: addShopLoading } = useCreateShopMutation({});
  const { mutate: createShopSectionMutation } = useCreateShopSectionMutation({ fetchPolicy: "network-only" });
  const { mutate: deleteShopSectionMutation } = useDeleteShopSectionMutation({ fetchPolicy: "network-only" });
  const { mutate: updateShopSectionMutation } = useUpdateShopSectionMutation({ fetchPolicy: "network-only" });
  const { mutate: sortShopSectionMutation } = useSortShopSectionMutation({ fetchPolicy: "network-only" });
  const { mutate: createShopItemMutation } = useCreateShopItemMutation({ fetchPolicy: "network-only" });
  const { mutate: reOrderShopItemMutation } = useReOrderShopItemsMutation({ fetchPolicy: "network-only" });
  const { mutate: deleteShopItemMutation } = useDeleteShopItemMutation({ fetchPolicy: "network-only" });
  const { mutate: updateShopItemMutation } = useUpdateShopItemMutation({ fetchPolicy: "network-only" });
  const { mutate: duplicateShopItemMutation } = useDuplicateShopItemMutation({ fetchPolicy: "network-only" });
  const { mutate: updateShopMutation } = useUpdateShopMutation({ fetchPolicy: "network-only" });
  const { mutate: deleteShopMutation } = useDeleteShopMutation({ fetchPolicy: "network-only" });
  const { mutate: duplicateShopMutation } = useDuplicateShopMutation({ fetchPolicy: "network-only" });

  const setActiveShop = (shop: Shop) => {
    activeShop.value = shop;
  };

  const addShop = async (input: CreateShopInput) => {
    const response: FetchResult<CreateShopMutation> | null = await createShopMutation({ versionId, input });
    try {
      if (response && response.data?.createShop) {
        const newShop = response.data.createShop as Shop;
        toastSuccess({
          detail: "Shop created successfully.",
          life: 3000,
          summary: "Success",
        });
        await refetchShops();
        const createdShop = shops.value.find((shop: Shop) => shop.id === newShop.id);
        setActiveShop(createdShop!);
      }
    } catch (error) {
      toastError({
        detail: "Failed to create shop. Please try again.",
        summary: "Error",
      });
    }
  };

  const updateShop = async (shopId: string, input: UpdateShopInput) => {
    const response: FetchResult<UpdateShopMutation> | null = await updateShopMutation({
      versionId,
      gameId: appId,
      shopId,
      input,
    });
    try {
      if (response && response.data?.updateShop) {
        const updatedShop = response.data.updateShop as Shop;
        toastSuccess({
          detail: "Shop updated successfully.",
          life: 3000,
          summary: "Success",
        });
        await refetchShops();
        const createdShop = shops.value.find((shop: Shop) => shop.id === updatedShop.id);
        setActiveShop(createdShop!);
      }
    } catch (error) {
      toastError({
        detail: "Failed to update shop. Please try again.",
        summary: "Error",
      });
    }
  };

  const addShopItem = async (input: CreateShopItemInput) => {
    const response: FetchResult<CreateShopItemMutation> | null = await createShopItemMutation({ input });
    try {
      if (response && response.data?.createShopItem) {
        toastSuccess({
          detail: "Shop item created successfully.",
          life: 3000,
          summary: "Success",
        });
        await refetchShops();
      }
    } catch (error) {
      toastError({
        detail: "Failed to create shop item. Please try again.",
        summary: "Error",
      });
    }

    return response?.data;
  };

  const duplicateShopItem = async (shopItemId: string, shopSectionId: string, shopId: string) => {
    const response = await duplicateShopItemMutation({
      shopId,
      shopItemId,
      sectionId: shopSectionId,
      versionId,
      gameId: appId,
    });

    try {
      if (response && response.data?.DuplicateShopItem) {
        toastSuccess({
          detail: "Shop item duplicated successfully.",
          life: 3000,
          summary: "Success",
        });
        await refetchShops();
      }
    } catch (error) {
      toastError({
        detail: "Failed to duplicate shop item. Please try again.",
        summary: "Error",
      });
    }

    return response?.data;
  };

  const deleteShopItem = async (shopItemId: string, shopSectionId: string, shopId: string) => {
    const response: FetchResult<DeleteShopItemMutation> | null = await deleteShopItemMutation({
      shopId,
      shopItemId,
      sectionId: shopSectionId,
      versionId,
      gameId: appId,
    });
    try {
      if (response && response.data?.DeleteShopItem) {
        toastSuccess({
          detail: "Shop item deleted successfully.",
          life: 3000,
          summary: "Success",
        });
        await refetchShops();
      }
    } catch (error) {
      toastError({
        detail: "Failed to delete shop item. Please try again.",
        summary: "Error",
      });
    }

    return response?.data;
  };

  const deleteShop = async (shopId: string) => {
    const response: FetchResult<DeleteShopMutation> | null = await deleteShopMutation({
      versionId,
      gameId: appId,
      shopId,
    });
    if (response?.data?.DeleteShop?.success) {
      toastSuccess({
        detail: "Shop deleted successfully.",
        life: 3000,
        summary: "Success",
      });
      await refetchShops();
    } else {
      toastError({
        detail: response?.data?.DeleteShop?.errorMessage || "Failed to delete shop. Please try again.",
        summary: "Error",
      });
    }

    return response?.data;
  };

  const duplicateShop = async (shopId: string) => {
    await duplicateShopMutation({
      versionId,
      gameId: appId,
      shopId,
    });

    await refetchShops();
  };

  const updateShopItem = async (input: UpdateShopItemInput) => {
    const response: FetchResult<UpdateShopItemMutation> | null = await updateShopItemMutation({
      input,
    });
    try {
      if (response && response.data?.UpdateShopItem) {
        toastSuccess({
          detail: "Shop item updated successfully.",
          life: 3000,
          summary: "Success",
        });
        await refetchShops();
      }
    } catch (error) {
      toastError({
        detail: "Failed to update shop item. Please try again.",
        summary: "Error",
      });
    }

    return response?.data;
  };
  const isDuplicateName = (name: string) => shops.value.some((shop: Shop) => shop.name === name);

  const isDuplicateShopSectionName = (name: string, shopId: string) => {
    const selectedShop = shops.value.find((shop: Shop) => shop.id === shopId);
    return !!selectedShop && selectedShop.sections.some((section: ShopSection) => section.name === name);
  };

  const addShopSection = async (shopId: string, input: CreateShopSectionPayload) => {
    try {
      const response: FetchResult<CreateShopSectionMutation> | null = await createShopSectionMutation({
        versionId,
        input,
        shopId,
        gameId: appId,
      });

      if (response && response.data?.createShopSection) {
        toastSuccess({
          detail: "Section created successfully.",
          life: 3000,
          summary: "Success",
        });

        await refetchShops();
      }
    } catch (error) {
      console.error(error);
      toastError({
        detail: "Failed to create shop section. Please try again.",
        summary: "Error",
        life: 3000,
      });
    }
  };

  const updateShopSection = async (shopId: string, shopSectionId: string, input: UpdateShopSectionPayload) => {
    try {
      const response: FetchResult<UpdateShopSectionMutation> | null = await updateShopSectionMutation({
        versionId,
        input,
        shopId,
        gameId: appId,
        shopSectionId,
      });

      if (response && response.data?.updateShopSection) {
        await refetchShops();
      }
    } catch (error) {
      console.error(error);
      toastError({
        detail: "Failed to update shop section. Please try again.",
        summary: "Error",
        life: 3000,
      });
    }
  };

  const sortShopSection = async (shopId: string, input: SortShopSectionPayload) => {
    try {
      const response: FetchResult<SortShopSectionMutation> | null = await sortShopSectionMutation({
        versionId,
        input,
        shopId,
        gameId: appId,
      });

      if (response && response.data?.SortShopSection) {
        await refetchShops();
      }
    } catch (error) {
      console.error(error);
      toastError({
        detail: "Failed to re-order shop section. Please try again.",
        summary: "Error",
        life: 3000,
      });
    }
  };

  const deleteShopSection = async (shopId: string, shopSectionId: string) => {
    try {
      const response: FetchResult<DeleteShopSectionMutation> | null = await deleteShopSectionMutation({
        versionId,
        shopId,
        gameId: appId,
        shopSectionId,
      });

      if (response && response.data?.deleteShopSection) {
        toastSuccess({
          detail: "Section deleted successfully.",
          life: 3000,
          summary: "Success",
        });

        await refetchShops();
      }
    } catch (error) {
      console.error(error);
      toastError({
        detail: "Failed to delete shop section. Please try again.",
        summary: "Error",
        life: 3000,
      });
    }
  };

  const sortShopItem = async (shopId: string, sectionId: string, shopItemId: string, input: ReOrderShopItemInput) => {
    try {
      const response: FetchResult<ReOrderShopItemsMutation> | null = await reOrderShopItemMutation({
        versionId,
        input,
        shopId,
        sectionId,
        shopItemId,
        gameId: appId,
      });

      if (response && response.data?.reOrderShopItems) {
        await refetchShops();
      }
    } catch (error) {
      console.error(error);
      toastError({
        detail: "Failed to re-order shop items. Please try again.",
        summary: "Error",
        life: 3000,
      });
    }
  };

  return {
    shopsToDisplay,
    activeShop,
    addShop,
    queryLoading,
    addShopLoading,
    setActiveShop,
    isDuplicateName,
    refetchShops,
    isDuplicateShopSectionName,
    addShopSection,
    deleteShopSection,
    updateShopSection,
    sortShopSection,
    duplicateShop,
    addShopItem,
    sortShopItem,
    deleteShopItem,
    updateShopItem,
    updateShop,
    deleteShop,
    duplicateShopItem,
  };
}
